import React, { useState, useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';

import type Trip from '@this/domain/trip/trip2';
import type Order from '@this/domain/order2';
import type OrderItem from '@this/domain/order_item2';
import type { Element } from '@this/domain/order_item2';
import TransportElement from '@this/domain/transport_element2';
import HotelElement from '@this/domain/hotel_element2';
import type ShareholderTicketList from '@this/domain/shareholder_ticket_list/index';
import type BulkTicket from '@this/domain/bulk_ticket2';
import type TaxType from '@this/domain/tax_type';
import type PaymentMethodList from '@this/src/domain/payment_method/payment_method_list';

import Text from '@this/shared/text/text';
import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import Modal from '@this/shared/modal/modal';
import Confirm from '@this/shared/confirm/confirm';
import { Button } from '@this/shared/ui/inputs/button';
import type { ShareholderInfo } from '@this/domain/other_service_shareholder_info';
import type { CabinOption } from '@this/domain/cabin_option';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import OrderItemPriceDetail from '@this/domain/order_item_price_detail2';
import { Fetcher, HTTPError } from '@this/src/util';
import type { IndividualDomesticAirSuppliedItem } from '@this/domain/individual_domestic_air_supplied_item';
import { reportError } from '@this/lib/bugsnag';
import TransportElementTerminal from '@this/src/domain/transport_element_terminal/transport_element_terminal2';
import { observer } from 'mobx-react';
import { useAITTheme } from '@this/src/components/shared/ui/theme';
import TimeZoneLib from '../../../../../../lib/time_zone.json';
import PartnerErrorMessage from '../../partner_error_message';
import type {
  PnrRetrieveFlightResponseJson,
  PnrRetrievePriceResponseJson
} from './price_change_form_item/price_change_form_item';
import PriceChangeFormItem from './price_change_form_item/price_change_form_item';

const utils = require('@this/src/utils');

export type TimeZone = {
  label: string;
  value: string;
};

interface Props {
  serviceId: number;
  selectedTrip: Trip;
  order?: Order;
  shareholderTickets: ShareholderTicketList;
  bulkTickets: BulkTicket[];
  taxTypes: TaxType[];
  paymentMethods: PaymentMethodList;
  shareholderInfos: ShareholderInfo[];
  cabinOptions: CabinOption[];
  individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[];
  onSubmitted: () => void;
  reloadSelectedTrip: () => void;
  hotelElementProviderOptions: { [key: string]: string };
}

const PriceChangeForm: React.FC<Props> = ({
  serviceId,
  selectedTrip,
  order,
  shareholderTickets,
  bulkTickets,
  taxTypes,
  paymentMethods,
  shareholderInfos,
  cabinOptions,
  individualTargetSuppliedItems,
  individualDomesticAirSuppliedItems,
  onSubmitted,
  reloadSelectedTrip,
  hotelElementProviderOptions
}) => {
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMarginAmountNotification, setShowMarginAmountNotification] = useState(false);
  const [cancelHotels, setCancelHotels] = useState<HotelElement[]>([]);
  const [errors, setErrors] = useState<string[][][]>([]);
  const [paymentError, setPaymentError] = useState<string | undefined>();
  const timeZones = TimeZoneLib as TimeZone[];
  const { theme } = useAITTheme();

  const isAirNumbersMissing = useCallback(() => {
    let missing = false;
    if (utils.isAiTravel(serviceId) && selectedTrip.isFixed && order) {
      _.each(order.orderItems, item => {
        _.each(item.elements, el => {
          if (
            el.type === 'transport' &&
            el instanceof TransportElement &&
            el.isDomesticAir &&
            el.isAirNumbersMissing
          ) {
            missing = true;
          }
        });
      });
    }
    return missing;
  }, [serviceId, selectedTrip.isFixed, order]);

  const [isAirNumbersMissingState, setIsAirNumbersMissingState] = useState(isAirNumbersMissing());

  const hasValidationError = useCallback(
    (errs?: string[][][]): boolean => {
      const targetErrors = errs ?? errors;
      for (let i = 0; i < targetErrors.length; i += 1) {
        const err = targetErrors[i];
        for (let j = 0; j < err.length; j += 1) {
          const e = err[j];
          if (e && e.length > 0) {
            return true;
          }
        }
      }
      return false;
    },
    [errors]
  );

  const openConfirm = useCallback(() => {
    if (!order) {
      return;
    }
    const validationErrors = order.validationErrors;
    setErrors(validationErrors);
    setIsConfirmVisible(!hasValidationError(validationErrors));
  }, [order, hasValidationError]);

  const closeConfirm = useCallback(() => {
    setIsConfirmVisible(false);
    setPaymentError('');
  }, []);

  const handleConfirmMarginAmountNotification = useCallback(() => {
    setShowMarginAmountNotification(false);
    openConfirm();
  }, [openConfirm]);

  const openConfirmWithMarginAmountNotification = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const needReCalcMarginAmount = order ? order.isNeedReCalcMarginAmount() : false;
      if (needReCalcMarginAmount) {
        setShowMarginAmountNotification(true);
      } else {
        openConfirm();
      }
    },
    [order, openConfirm]
  );
  const requestParams = useCallback((): object => {
    if (!order) {
      return {};
    }

    return _.merge(order.updateParams, { trip_id: selectedTrip.id });
  }, [order, selectedTrip.id]);

  const addToCancelList = useCallback((element: Element, itemCategory: string) => {
    if (
      element instanceof HotelElement &&
      element.initialData?.provider === 'mynavi' &&
      itemCategory !== 'cancel'
    ) {
      if (element.checkinDate >= moment.tz('Asia/Tokyo').add(1, 'day').startOf('day')) {
        setCancelHotels(prev => [...prev, element]);
      } else {
        return [[['変更受付期間を過ぎているため、予約のキャンセルは行えません。']]];
      }
    }
    return [];
  }, []);

  const handleRemoveItem = useCallback(
    (orderItem: OrderItem) => {
      let errorsList: string[][][] = [];
      orderItem.elements.some(element => {
        errorsList = addToCancelList(element, orderItem.orderItemCategory);
        return errorsList.length > 0;
      });

      if (order && errorsList.length === 0) {
        order.handleRemoveItem(orderItem);
      } else {
        setErrors(errorsList);
      }
    },
    [order, addToCancelList]
  );

  const handleRemoveElement = useCallback(
    (orderItem: OrderItem, i: number) => {
      let errorsList: string[][][] = [];
      const element = orderItem.elements[i];
      errorsList = addToCancelList(element, orderItem.orderItemCategory);

      if (errorsList.length === 0) {
        orderItem.handleRemoveElement(element);
      }
      setErrors(errorsList);
    },
    [addToCancelList]
  );

  const updatePriceDetailsFromPnr = useCallback(
    (orderItem: OrderItem, pnrPriceResponseJsonArray: PnrRetrievePriceResponseJson[]): void => {
      orderItem.isEditing = true;
      const priceDetails = orderItem.price?.priceDetails;

      if (!priceDetails) return;

      const updatedPriceDetails: OrderItemPriceDetail[] = [];

      pnrPriceResponseJsonArray.forEach((priceDetail: PnrRetrievePriceResponseJson) => {
        const existingDetail = priceDetails.find(detail => detail.name === priceDetail.name);

        if (existingDetail) {
          existingDetail.price = priceDetail.price;
          existingDetail.taxTypeId = priceDetail.tax_type_id;
          updatedPriceDetails.push(existingDetail);
        } else {
          const newPriceDetail = new OrderItemPriceDetail({
            name: priceDetail.name || '',
            price: priceDetail.price || 0,
            orderItemPriceId: orderItem.price?.id || 0,
            taxTypeId: priceDetail.tax_type_id || 0
          });
          updatedPriceDetails.push(newPriceDetail);
        }
      });

      if (orderItem.price) {
        orderItem.price.priceDetails = updatedPriceDetails;
      }
    },
    []
  );

  const updateForeignAirTransportElements = useCallback(
    (orderItem: OrderItem, responseSegments: PnrRetrieveFlightResponseJson[]) => {
      orderItem.isEditing = true;
      orderItem.elements = orderItem.elements.filter(element => {
        const isForeignAir = element instanceof TransportElement && element.isForeignAir;

        return !isForeignAir;
      });

      const newElements = responseSegments.map(
        segment =>
          new TransportElement({
            transportType: 'foreign_air',
            from: new TransportElementTerminal({
              name: segment.airport_from_name,
              time: moment(segment.d_datetime).tz(segment.from_time_zone),
              timeZone: segment.from_time_zone
            }),
            to: new TransportElementTerminal({
              name: segment.airport_to_name,
              time: moment(segment.a_datetime).tz(segment.to_time_zone),
              timeZone: segment.to_time_zone
            }),
            name: segment.carrier + segment.flight_number,
            cabin: segment.cabin
          })
      );

      orderItem.elements.push(...newElements);
    },
    []
  );

  const executeSubmit = useCallback(() => {
    Fetcher.post('/orders', requestParams()).then(
      () => {
        setIsSubmitting(false);
        setIsConfirmVisible(false);
        onSubmitted();
      },
      error => {
        setIsSubmitting(false);
        setPaymentError(
          error instanceof HTTPError &&
            [400, 409, 500].includes(error.response?.status ?? 0) &&
            error.response?.data.errors
            ? error.response.data.errors
            : ''
        );
      }
    );
  }, [requestParams, onSubmitted]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    let errorsList: string[][][] = [];

    if (cancelHotels.length > 0) {
      cancelHotels.forEach((element, i) => {
        Fetcher.put(`/hotels/${element.id}/cancel`, {}).then(
          () => {
            if (element.provider === 'mynavi') {
              element.provider = 'other';
            }
            if (cancelHotels.length === i + 1) {
              setCancelHotels([]);
              setErrors(errorsList);
              executeSubmit();
            }
          },
          error => {
            element.restore();
            errorsList = [
              [
                [
                  error instanceof HTTPError && error.response?.data.error
                    ? error.response.data.error
                    : '通信環境が不安定です。\n時間をおいてもう一度お試しください。'
                ]
              ]
            ];
            if (cancelHotels.length === i + 1) {
              setCancelHotels([]);
              setErrors(errorsList);
              executeSubmit();
            }
          }
        );
      });
    } else {
      executeSubmit();
    }
  }, [cancelHotels, executeSubmit]);

  const updateIsAirNumbersMissing = useCallback(
    (orderItem: OrderItem) => {
      if (order) {
        order.orderItems = order.orderItems.map((item: OrderItem) => {
          if (item.id === orderItem.id) {
            return orderItem;
          }
          return item;
        });
        setIsAirNumbersMissingState(isAirNumbersMissing());
      }
    },
    [order, isAirNumbersMissing]
  );

  try {
    return (
      <>
        {order ? (
          <form
            className="virtual-counter-partner__pay-request-form"
            onSubmit={e => openConfirmWithMarginAmountNotification(e)}
          >
            {order.orderItems
              .filter(item => !item.deletedAt)
              .map((item, i) => (
                <PriceChangeFormItem
                  key={i}
                  orderItem={item}
                  index={i}
                  order={order}
                  selectedTrip={selectedTrip}
                  shareholderTickets={shareholderTickets}
                  bulkTickets={bulkTickets}
                  taxTypes={taxTypes}
                  serviceId={serviceId}
                  paymentMethods={paymentMethods}
                  shareholderInfos={shareholderInfos}
                  cabinOptions={cabinOptions}
                  individualTargetSuppliedItems={individualTargetSuppliedItems}
                  individualDomesticAirSuppliedItems={individualDomesticAirSuppliedItems}
                  handleRemoveItem={handleRemoveItem}
                  handleRemoveElement={handleRemoveElement}
                  updateIsAirNumbersMissing={updateIsAirNumbersMissing}
                  reloadSelectedTrip={reloadSelectedTrip}
                  hotelElementProviderOptions={hotelElementProviderOptions}
                  carProviderOptions={order.carProviderOptions}
                  carTypeOptions={order.carTypeOptions}
                  shippingCategoryOptions={order.shippingCategoryOptions}
                  wifiProviderOptions={order.wifiProviderOptions}
                  updateForeignAirTransportElements={updateForeignAirTransportElements}
                  updatePriceDetailsFromPnr={updatePriceDetailsFromPnr}
                  timeZones={timeZones}
                />
              ))}
            <div style={{ marginTop: '20px' }} data-testid="add-order-item-button">
              <Button color="sub" style={{ width: '90px' }} onClick={() => order.handleAddItem()}>
                +商品を追加
              </Button>
            </div>
            {hasValidationError() && (
              <div className="virtual-counter-partner__order-error-list">
                {errors.map((itemErrors, index) => (
                  <div key={index}>
                    {itemErrors.map((elementErrors, i) => (
                      <div className="error virtual-counter-partner__order-error(" key={i}>
                        {elementErrors.map((error, j) => (
                          <Text key={j} text={error} />
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <Button type="submit" disabled={isSubmitting || isAirNumbersMissingState}>
              変更内容を保存
            </Button>
            {isAirNumbersMissingState && (
              <div style={{ color: theme.tokens.colors.danger.primary }}>
                ※国内航空券の予約番号、または確認番号が未入力です。
              </div>
            )}
            <Modal hideModal={() => closeConfirm()} show={isConfirmVisible} title="値段変更の確認">
              <p>この内容で決済金額を変更します。</p>
              <div className="virtual-counter__pay-request-modal">
                <Text text={order.description} />
                <p>---</p>
                <br />
                <p className="bold">{`合計：${utils.formatPrice(order.totalPrice)}`}</p>
              </div>
              {paymentError && <PartnerErrorMessage message={paymentError} />}
              {isSubmitting ? (
                <SimpleLoading />
              ) : paymentError ? (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px' }}>
                  <Button onClick={() => closeConfirm()}>閉じる</Button>
                </div>
              ) : (
                <Confirm onConfirmed={() => handleSubmit()} onAborted={() => closeConfirm()} />
              )}
            </Modal>
          </form>
        ) : null}
        <Modal hideModal={() => setShowMarginAmountNotification(false)} show={showMarginAmountNotification}>
          <p>自動計算された手数料と入力された手数料が異なります。このまま保存しますか？</p>
          <Confirm
            onConfirmed={() => handleConfirmMarginAmountNotification()}
            onAborted={() => setShowMarginAmountNotification(false)}
          />
        </Modal>
      </>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

export default observer(PriceChangeForm);
